/**
 * features/admin/UserManagement.tsx
 * System-admin-only. Add teammates by email + role, change roles,
 * deactivate, or remove. Access is enforced at the Shell (this tab
 * only renders for an admin) and should be mirrored in Firestore
 * security rules.
 */
import { useMemo, useState } from 'react';
import { UserCog, UserPlus, X, Trash2, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from './useUsers';
import { createUser, updateUserRole, setUserActive, deleteUser, emailId } from './usersWrite';
import { ROLE_LABEL, ROLE_DESCRIPTION, ASSIGNABLE_ROLES, isAdmin } from '@/lib/permissions';
import { glassSurface } from '@/components/ui/glass';
import { Button, Card, Field, Input, Select, Badge, Spinner } from '@/components/ui/primitives';
import type { UserRole } from '@/types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddUserModal({ existing, onClose }: { existing: Set<string>; onClose: () => void }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('sales');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    const id = emailId(email);
    if (!EMAIL_RE.test(id)) {
      setError('Enter a valid email address.');
      return;
    }
    if (existing.has(id)) {
      setError('A user with that email already exists.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await createUser({ email: id, name, role }, user?.email ?? 'admin');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the user.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-12 w-full max-w-md">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <UserPlus size={18} />
              <h2 className="text-base font-semibold text-white">Add user</h2>
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Email" hint="They sign in with this Google or email account.">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@email.com" />
          </Field>
          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          </Field>
          <p className="rounded-xl bg-brandSoft px-3 py-2 text-xs text-primaryLight dark:bg-white/5 dark:text-gray-400">
            {ROLE_DESCRIPTION[role]}
          </p>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              Add user
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function UserManagement() {
  const { user, role } = useAuth();
  const { users, loading } = useUsers();
  const [adding, setAdding] = useState(false);

  const existing = useMemo(() => new Set(users.map((u) => u.id)), [users]);

  if (!isAdmin(role)) {
    return (
      <Card className="py-16 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-900/20">
          <ShieldAlert size={22} />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">Only the system admin can manage users.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-primaryLight dark:text-gray-400">
          You’re signed in as <span className="font-semibold text-primary dark:text-gray-100">{user?.email}</span> · System Admin
        </p>
        <Button icon={<UserPlus size={16} />} onClick={() => setAdding(true)}>
          Add user
        </Button>
      </div>

      {loading ? (
        <Spinner label="Loading users…" />
      ) : users.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <UserCog size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No teammates yet. Add a bookkeeper or sales user to get started.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-black/5 last:border-0 dark:border-white/5">
                  <td className="px-4 py-3">
                    {u.displayName && <div className="font-semibold text-primary dark:text-gray-100">{u.displayName}</div>}
                    <div className="text-xs text-primaryLight dark:text-gray-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onChange={(e) => void updateUserRole(u.id, e.target.value as UserRole)} className="text-xs">
                      {ASSIGNABLE_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => void setUserActive(u.id, !u.active)}>
                      <Badge tone={u.active ? 'success' : 'neutral'}>{u.active ? 'Active' : 'Disabled'}</Badge>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove ${u.email}? They will lose access.`)) void deleteUser(u.id);
                      }}
                      title="Remove user"
                      className="rounded-lg p-1.5 text-primaryLight transition hover:bg-red-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-white/5"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <p className="text-xs text-primaryLight dark:text-gray-500">
        New users sign in with the email you enter (Google or email/password). Access takes effect on their next sign-in.
      </p>

      {adding && <AddUserModal existing={existing} onClose={() => setAdding(false)} />}
    </div>
  );
}
